/**
 * courseUrlState.js
 *
 * Serializes and deserializes course configuration to/from URL query params
 * so that sharing the URL is enough to recreate the exact course on any device.
 *
 * Param keys are kept short to keep URLs readable.
 * Example URL:
 *   /?ct=T_LEEWARD&wd=270&d=2000&lat=59.324000&lng=18.071000&g3=1&g4=1
 */

/** Defaults mirror EditCourse initial state */
const DEFAULTS = {
  courseType: 'Leeward',
  windDirection: 0,
  distance: 2000,
  leewardDistance: 93,
  windwardBeats: 2,
  gateWidth: 6,
  finishAngle: 70,
  finishDistance: 93,
  finishType: 'Start finish together',
  numberOfBoats: 10,
  averageBoatLength: 7,
  startLineScaleFactor: 1.3,
  gate1: false,
  gate3: false,
  gate4: false,
  spinnaker: false,
  // Default start location: Stockholm archipelago
  lat: 59.324,
  lng: 18.071,
  // Triangle course
  triangleAngle: 60,
  // Offset
  offsetEnabled: false,
  offsetDistance: 74,
  offsetAngle: 80
};

// Normalise courseType from old key-format URLs (e.g. 'T_LEEWARD') to
// display-value format ('Leeward') that the calculator and validation expect.
const COURSE_TYPE_KEY_MAP = {
  T_LEEWARD:         'Leeward',
  T_WINDWARD:        'Windward',
  T_INNER_TRAPEZOID: 'Inner Trapezoid',
  T_OUTER_TRAPEZOID: 'Outer Trapezoid'
};
const normaliseCourseType = (ct) => COURSE_TYPE_KEY_MAP[ct] || ct;

/**
 * Read course state from current window URL query params.
 * Returns { courseData, location, offset, hasParams }.
 * hasParams is true when at least one course param was present (i.e. came from a shared link).
 */
export function readCourseStateFromUrl() {
  const p = new URLSearchParams(window.location.search);

  const get = (key, def) => {
    const v = p.get(key);
    return v !== null ? v : String(def);
  };

  const courseData = {
    courseType:             normaliseCourseType(get('ct', DEFAULTS.courseType)),
    windDirection:          parseFloat(get('wd', DEFAULTS.windDirection)),
    distance:               parseFloat(get('d',  DEFAULTS.distance)),
    leewardDistance:        parseFloat(get('ld', DEFAULTS.leewardDistance)),
    windwardBeats:          parseInt(  get('wb', DEFAULTS.windwardBeats), 10),
    gateWidth:              parseFloat(get('gw', DEFAULTS.gateWidth)),
    finishAngle:            parseFloat(get('fa', DEFAULTS.finishAngle)),
    finishDistance:         parseFloat(get('fd', DEFAULTS.finishDistance)),
    finishType:             get('ft', DEFAULTS.finishType),
    numberOfBoats:          parseInt(  get('nb', DEFAULTS.numberOfBoats), 10),
    averageBoatLength:      parseFloat(get('bl', DEFAULTS.averageBoatLength)),
    startLineScaleFactor:   parseFloat(get('sl', DEFAULTS.startLineScaleFactor)),
    gate1:         p.has('g1') ? p.get('g1') === '1' : DEFAULTS.gate1,
    gate3:         p.has('g3') ? p.get('g3') === '1' : DEFAULTS.gate3,
    gate4:         p.has('g4') ? p.get('g4') === '1' : DEFAULTS.gate4,
    spinnaker:     p.has('sp') ? p.get('sp') === '1' : DEFAULTS.spinnaker,
    triangleAngle: parseInt(get('ta', DEFAULTS.triangleAngle), 10)
  };

  const location = {
    lat: parseFloat(get('lat', DEFAULTS.lat)),
    lng: parseFloat(get('lng', DEFAULTS.lng))
  };

  const offset = {
    enabled:  p.has('oe') ? p.get('oe') === '1' : DEFAULTS.offsetEnabled,
    distance: parseFloat(get('od', DEFAULTS.offsetDistance)),
    angle:    parseFloat(get('oa', DEFAULTS.offsetAngle))
  };

  // Detect whether any course param was present (shared link vs fresh load)
  const hasParams = p.has('ct') || p.has('wd') || p.has('lat');

  return { courseData, location, offset, hasParams };
}

/** Build the full set of course + location query params as a URLSearchParams. */
function buildParams(courseData, location, offset) {
  const p = new URLSearchParams();
  p.set('ct',  courseData.courseType);
  p.set('wd',  courseData.windDirection);
  p.set('d',   courseData.distance);
  p.set('ld',  courseData.leewardDistance);
  p.set('wb',  courseData.windwardBeats);
  p.set('gw',  courseData.gateWidth);
  p.set('fa',  courseData.finishAngle);
  p.set('fd',  courseData.finishDistance);
  p.set('ft',  courseData.finishType);
  p.set('nb',  courseData.numberOfBoats);
  p.set('bl',  courseData.averageBoatLength);
  p.set('sl',  courseData.startLineScaleFactor);
  p.set('g1',  courseData.gate1     ? '1' : '0');
  p.set('g3',  courseData.gate3     ? '1' : '0');
  p.set('g4',  courseData.gate4     ? '1' : '0');
  p.set('sp',  courseData.spinnaker ? '1' : '0');
  p.set('ta',  courseData.triangleAngle ?? DEFAULTS.triangleAngle);
  p.set('lat', location.lat.toFixed(6));
  p.set('lng', location.lng.toFixed(6));
  p.set('oe',  offset.enabled  ? '1' : '0');
  p.set('od',  offset.distance);
  p.set('oa',  offset.angle);
  return p;
}

/** Build the course params for the map view (no lat/lng — GDPR). */
function buildMapParams(courseData, offset) {
  const p = new URLSearchParams();
  p.set('ct',  courseData.courseType);
  p.set('wd',  courseData.windDirection);
  p.set('d',   courseData.distance);
  p.set('ld',  courseData.leewardDistance);
  p.set('wb',  courseData.windwardBeats);
  p.set('gw',  courseData.gateWidth);
  p.set('fa',  courseData.finishAngle);
  p.set('fd',  courseData.finishDistance);
  p.set('ft',  courseData.finishType);
  p.set('nb',  courseData.numberOfBoats);
  p.set('bl',  courseData.averageBoatLength);
  p.set('sl',  courseData.startLineScaleFactor);
  p.set('g1',  courseData.gate1     ? '1' : '0');
  p.set('g3',  courseData.gate3     ? '1' : '0');
  p.set('g4',  courseData.gate4     ? '1' : '0');
  p.set('sp',  courseData.spinnaker ? '1' : '0');
  p.set('ta',  courseData.triangleAngle ?? DEFAULTS.triangleAngle);
  p.set('oe',  offset.enabled  ? '1' : '0');
  p.set('od',  offset.distance);
  p.set('oa',  offset.angle);
  return p;
}

/**
 * Write course + location state to the config page URL (/?...) using
 * history.replaceState. Always uses an absolute path so this is safe
 * to call from any route (e.g. when leaving /map).
 */
export function writeCourseStateToUrl(courseData, location, offset) {
  const p = buildParams(courseData, location, offset);
  window.history.replaceState({}, '', `/?${p.toString()}`);
}

/**
 * Build a shareable absolute URL including lat/lng so recipients can
 * recreate the exact course. Does NOT modify the browser URL.
 */
export function buildShareUrl(courseData, location, offset) {
  const p = buildParams(courseData, location, offset);
  return `${window.location.origin}/?${p.toString()}`;
}

/**
 * Switch the browser URL to /map?... (course params only, no lat/lng) so
 * GA4 sees a distinct page without receiving personal location data.
 * Uses replaceState — no extra history entry.
 */
export function enterMapView(courseData, offset) {
  const p = buildMapParams(courseData, offset);
  window.history.replaceState({}, '', `/map?${p.toString()}`);
}
